import React from 'react';
import { Button, Card, Col, DatePicker, Input, Row, Select, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { useGetClientsForFilterQuery } from '../../store/api/applicationsApi';
import { useGetCarriersQuery } from '../../store/api/referencesApi';
import {
  DOCUMENT_REGISTRY_CATEGORY_OPTIONS,
  DocumentRegistryFilterDraft,
} from '../../utils/documentRegistryUi';
import { SearchSelect } from '../../ui';

const { Text } = Typography;

type DocumentRegistryFiltersProps = {
  draftFilters: DocumentRegistryFilterDraft;
  dateRange: [Dayjs | null, Dayjs | null] | null;
  onDraftChange: (next: DocumentRegistryFilterDraft) => void;
  onDateRangeChange: (range: [Dayjs | null, Dayjs | null] | null) => void;
  onSearch: () => void;
  onReset: () => void;
};

const DocumentRegistryFilters: React.FC<DocumentRegistryFiltersProps> = ({
  draftFilters,
  dateRange,
  onDraftChange,
  onDateRangeChange,
  onSearch,
  onReset,
}) => {
  const { data: clientsData } = useGetClientsForFilterQuery();
  const { data: carriersData } = useGetCarriersQuery();

  return (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Row gutter={[12, 12]}>
        <Col xs={24} md={8} lg={6}>
          <Text type="secondary">Заявка</Text>
          <SearchSelect
            entityType="application"
            placeholder="Номер, клиент или № клиента"
            value={draftFilters.applicationId}
            onChange={(applicationId) =>
              onDraftChange({
                ...draftFilters,
                applicationId,
                internalNumber: undefined,
              })
            }
          />
        </Col>
        <Col xs={24} md={8} lg={6}>
          <Text type="secondary">Клиент</Text>
          <Select
            allowClear
            showSearch
            placeholder="Клиент"
            optionFilterProp="label"
            style={{ width: '100%' }}
            value={draftFilters.clientId}
            onChange={(value) => onDraftChange({ ...draftFilters, clientId: value })}
            options={(clientsData || []).map((c) => ({ value: c.id, label: c.name }))}
          />
        </Col>
        <Col xs={24} md={8} lg={6}>
          <Text type="secondary">Перевозчик</Text>
          <Select
            allowClear
            showSearch
            placeholder="Перевозчик"
            optionFilterProp="label"
            style={{ width: '100%' }}
            value={draftFilters.carrierId}
            onChange={(value) => onDraftChange({ ...draftFilters, carrierId: value })}
            options={(carriersData || []).map((c) => ({ value: c.id, label: c.name }))}
          />
        </Col>
        <Col xs={24} md={8} lg={6}>
          <Text type="secondary">Маршрут</Text>
          <SearchSelect
            entityType="route"
            placeholder="№ маршрута, перевозчик или договор"
            value={draftFilters.routeId}
            onChange={(routeId) => onDraftChange({ ...draftFilters, routeId })}
          />
        </Col>
        <Col xs={24} md={8} lg={6}>
          <Text type="secondary">Поиск</Text>
          <Input
            allowClear
            placeholder="Название или комментарий"
            value={draftFilters.search}
            onChange={(event) => onDraftChange({ ...draftFilters, search: event.target.value })}
          />
        </Col>
        <Col xs={24} md={8} lg={6}>
          <Text type="secondary">Тип документа</Text>
          <Select
            allowClear
            placeholder="Категория"
            style={{ width: '100%' }}
            value={draftFilters.documentCategory}
            onChange={(value) => onDraftChange({ ...draftFilters, documentCategory: value })}
            options={[...DOCUMENT_REGISTRY_CATEGORY_OPTIONS]}
          />
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Text type="secondary">Период</Text>
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            value={dateRange as any}
            onChange={(range) => onDateRangeChange(range as [Dayjs | null, Dayjs | null] | null)}
          />
        </Col>
        <Col xs={24} md={12} lg={8} style={{ display: 'flex', alignItems: 'flex-end' }}>
          <Space wrap>
            <Button type="primary" icon={<SearchOutlined />} onClick={onSearch}>
              Найти
            </Button>
            <Button onClick={onReset}>Сбросить</Button>
          </Space>
        </Col>
      </Row>
    </Card>
  );
};

export default DocumentRegistryFilters;
